import * as utils from '../../util';
import {TrackPageInterface} from '../interfaces';
import {analyticsConstants, constants} from '../../constants';

// Lodash
import * as lodash from 'lodash';

import {TelemetryGenericEnum} from '../../shared/enums';
import {TrackingUtilsService} from './tracking-utils.service';
import {TealiumService} from '../../modules/core/services/tealium.service';

export const trackPagePayloadCase = (
  tealiumService: TealiumService,
  trackingUtilsService: TrackingUtilsService,
  guestData: any,
): TrackPageInterface => {
  const utagData: TrackPageInterface = retrieveTrackPagePayloadBuilt(tealiumService, trackingUtilsService, guestData);
  return buildUtagDataToBeDispatched(utagData, trackingUtilsService);
}

const buildUtagDataToBeDispatched = (
  utagData: TrackPageInterface,
  trackingUtilsService: TrackingUtilsService,
): TrackPageInterface => {
  utagData = retrieveTrackingSspData(utagData, trackingUtilsService);
  utagData = retrieveGuestDetailsData(utagData, trackingUtilsService);
  return retrieveHealthCenterData(utagData, trackingUtilsService);
}

const retrieveTrackingSspData = (
  utagData: TrackPageInterface,
  trackingUtilsService: TrackingUtilsService,
) => {
  if (lodash.get(utagData, 'pageName', null) === constants.PAGE_NAME_FOR_TRACKING.SSP) {
    utagData['olciPassengerIds'] = trackingUtilsService.getSelectedGuestId();
  }
  return utagData;
}

const retrieveGuestDetailsData = (
  utagData: TrackPageInterface,
  trackingUtilsService: TrackingUtilsService,
) => {
  if (lodash.get(utagData, 'pageName', null) === constants.PAGE_NAME_FOR_TRACKING.GUEST_DETAILS) {
    utagData['olciPassengerIds'] = trackingUtilsService.getSelectedGuest();
    utagData['fluidVariable'] = trackingUtilsService.getTrackingGuestStatus();
  }
  return utagData;
}

const retrieveHealthCenterData = (
  utagData: TrackPageInterface,
  trackingUtilsService: TrackingUtilsService,
) => {
  if (lodash.get(utagData, 'pageName', null) === constants.PAGE_NAME_FOR_TRACKING.HEALTH_CENTER) {
    utagData['pageName'] = constants.PAGE_NAME_FOR_TRACKING.CHECK_IN_HEALTH_CENTER;
    utagData['platform'] = trackingUtilsService.getPlatform();
    utagData['loginStatus'] = constants.LOGIN_STATUS.LOGGED_IN;
    utagData['consumerId'] = utagData['passengerId'];
  }
  return utagData;
}

const retrieveTrackPagePayloadBuilt = (
  tealiumService: TealiumService,
  trackingUtilsService: TrackingUtilsService,
  guestData: any,
): TrackPageInterface => ({
  pageName: tealiumService.getCurrentPage(),
  siteSection: analyticsConstants.APP_NAME,
  siteType: analyticsConstants.SITE_TYPE,
  country: utils.getCountryCode() ?? analyticsConstants.DEFAULT_COUNTRY,
  language: trackingUtilsService.getCurrentLanguageCode().toLowerCase() ?? analyticsConstants.DEFAULT_LANGUAGE,
  brand: trackingUtilsService.getBrandCode(),
  destination: guestData?.destinationPort ?? TelemetryGenericEnum.EMPTY_VALUE,
  cruiseLength: guestData?.sailingDays ?? TelemetryGenericEnum.EMPTY_VALUE,
  departureDate: guestData?.departureDate ?? TelemetryGenericEnum.EMPTY_VALUE,
  shipDate: trackingUtilsService.getDateNoDashes(),
  departurePort: guestData?.departurePort ?? TelemetryGenericEnum.EMPTY_VALUE,
  daysToCruise: guestData?.daysLeft ?? TelemetryGenericEnum.EMPTY_VALUE,
  bookingDesc: utils.capitalizeString(guestData?.cruiseName),
  shipName: guestData?.shipName ?? TelemetryGenericEnum.EMPTY_VALUE,
  shipCode: trackingUtilsService.getSimplifiedShipName(),
  bookingId: retrieveBookingId(guestData?.bookingId),
  voyageId: trackingUtilsService.getSimplifiedShipName() + trackingUtilsService.getDateNoDashes(),
  loyaltyNum: trackingUtilsService.getLoyaltyNum(guestData?.loggedInPaxId),
  vdsId: trackingUtilsService.getVdsId(),
  passengerId: guestData?.loggedInPaxId ?? constants.DEFAULT_GUEST_ID_FOR_TRACKING.ID,
  boardingPassId: trackingUtilsService.getSspBarCode(),
  stateRoom: trackingUtilsService.getSspStateRoom(),
  aamId: retrieveAamId(guestData?.bookingId, guestData?.departureDate, trackingUtilsService),
  associatedGuests: retrieveAssociatedGuests(trackingUtilsService, guestData),
  reservationGuests: retrieveReservationGuests(trackingUtilsService, guestData),
  totalGuests: retrieveTotalGuests(trackingUtilsService),
  associatedReservations: retrieveAssociatedReservations(trackingUtilsService, guestData),
  bookingCruiseType: trackingUtilsService.getBookingType(),
  associatedGuestsCheckedIn: retrieveAssociatedGuestsCheckedIn(trackingUtilsService),
  reservationGuestsCheckedIn: retrieveReservationGuestsCheckedIn(trackingUtilsService),
  totalGuestsCheckedIn: retrieveTotalGuestsCheckedIn(trackingUtilsService),
  associatedGuestsCheckingIn: retrieveAssociatedGuestsCheckingIn(trackingUtilsService),
  reservationGuestsCheckingIn: retrieveReservationGuestsCheckingIn(trackingUtilsService),
  totalGuestsCheckingIn: retrieveTotalGuestsCheckingIn(trackingUtilsService),
  numberGuests: retrieveNumberGuests(trackingUtilsService),
});

const retrieveBookingId = (bookingId: any) => bookingId ?? TelemetryGenericEnum.EMPTY_VALUE;
const retrieveAamId = (
  bookingId: any, departureDate: string, trackingUtilsService: TrackingUtilsService
) => bookingId
    ? retrieveBookingId(bookingId) + '|' + departureDate + '|' + trackingUtilsService.getSimplifiedShipName()
    : departureDate + '|' + trackingUtilsService.getSimplifiedShipName();

export const retrieveAssociatedGuests = (
  trackingUtilsService: TrackingUtilsService,
  guestData: any,
) => trackingUtilsService.getAssociatedAdults(guestData?.bookingId).length +
    '>' +
    trackingUtilsService.getAssociatedMinors(guestData?.bookingId).length +
    '>' +
    trackingUtilsService.getAssociatedGuests(guestData?.bookingId).length;

export const retrieveReservationGuests = (
  trackingUtilsService: TrackingUtilsService,
  guestData: any,
) => trackingUtilsService.getReservationAdults(guestData?.bookingId).length +
  '>' +
  trackingUtilsService.getReservationMinors(guestData?.bookingId).length +
  '>' +
  trackingUtilsService.getReservationGuests(guestData?.bookingId).length;

export const retrieveTotalGuests = (
  trackingUtilsService: TrackingUtilsService,
) => trackingUtilsService.getListOfAdults().length + '>' + trackingUtilsService.getListOfMinors().length + '>' + trackingUtilsService.getGuestList().length;

export const retrieveAssociatedReservations = (
  trackingUtilsService: TrackingUtilsService,
  guestData: any
) => trackingUtilsService.getNumberOfAssociatedReservations(guestData?.bookingId).length

export const retrieveAssociatedGuestsCheckedIn = (
  trackingUtilsService: TrackingUtilsService,
) => trackingUtilsService.getAssociatedAdultsCheckedIn().length +
  '>' +
  trackingUtilsService.getAssociatedMinorsCheckedIn().length +
  '>' +
  trackingUtilsService.getAssociatedGuestsCheckedIn().length;

export const retrieveReservationGuestsCheckedIn = (
  trackingUtilsService: TrackingUtilsService,
) => trackingUtilsService.getReservationAdultsCheckedIn().length +
  '>' +
  trackingUtilsService.getReservationMinorsCheckedIn().length +
  '>' +
  trackingUtilsService.getReservationGuestsCheckedIn().length

export const retrieveTotalGuestsCheckedIn = (
  trackingUtilsService: TrackingUtilsService,
) =>
  trackingUtilsService.getTotalAdultsCheckedIn().length + '>' +
  trackingUtilsService.getTotalMinorsCheckedIn().length + '>' +
  trackingUtilsService.getCompletedGuestList().length;

export const retrieveAssociatedGuestsCheckingIn = (
  trackingUtilsService: TrackingUtilsService,
) => trackingUtilsService.getAssociatedAdultsCheckingIn().length +
  '>' +
  trackingUtilsService.getAssociatedMinorsCheckingIn().length +
  '>' +
  trackingUtilsService.getAssociatedGuestsCheckingIn().length;

export const retrieveReservationGuestsCheckingIn = (
  trackingUtilsService: TrackingUtilsService,
) => trackingUtilsService.getReservationAdultsCheckingIn().length +
  '>' +
  trackingUtilsService.getReservationMinorsCheckingIn().length +
  '>' +
  trackingUtilsService.getReservationGuestsCheckingIn().length;

export const retrieveTotalGuestsCheckingIn = (
  trackingUtilsService: TrackingUtilsService,
) => trackingUtilsService.getTotalAdultsCheckingIn().length +
  '>' +
  trackingUtilsService.getTotalMinorsCheckingIn().length +
  '>' +
  trackingUtilsService.getGuestsSelectedForCheckin().length;

export const retrieveNumberGuests = (
  trackingUtilsService: TrackingUtilsService,
) => trackingUtilsService.getReservationGuestsCheckingIn().length + '>' +
  trackingUtilsService.getAssociatedGuestsCheckingIn().length;
